/**
 * OmegaFounderSpecsPage — /omega/founder-specs
 * Sprint 42 A6: Mode-B founder product-spec authoring. The Founder commands
 * "build THIS for THIS market/client" — name, target market/client, requirements,
 * constraints, budget. Founder-signs the canonical spec and POSTs to
 * /admin/pipeline/founder-spec (A-01). The run then appears in /omega/pipeline,
 * parked at the Spec→Test gate awaiting the Founder's approval.
 */
import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { apiFetch } from '@/api/http';

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const lines = (s: string): string[] =>
  s.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  marginTop: 4,
  boxSizing: 'border-box',
};

export function OmegaFounderSpecsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [name, setName] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [targetClient, setTargetClient] = useState('');
  const [requirements, setRequirements] = useState('');
  const [constraints, setConstraints] = useState('');
  const [budgetUsd, setBudgetUsd] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ run_id: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setResult(null);
    if (!name.trim() || !targetMarket.trim()) {
      setError('Name and target market are required.');
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        target_market: targetMarket.trim(),
        target_client: targetClient.trim() || null,
        requirements: lines(requirements),
        constraints: lines(constraints),
        budget_usd_cents: Math.round(Number(budgetUsd || '0') * 100),
      };
      // This route is gated by `require_founder`, whose Path A verifies
      // `X-Founder-Signature` over the FIXED founder access challenge
      // (SHA-256("founder-omega-access-v1")) — NOT the request body. Signing the
      // body returns 403. Sign the access challenge so the gate accepts the Founder;
      // the spec body is still POSTed (apiFetch canonicalises + operator-signs it)
      // and the run is engine-signed server-side.
      const sig = await signHashWithFounderKey(await sha256hex('founder-omega-access-v1'));
      const data = await apiFetch<{ run_id: string }>('/admin/pipeline/founder-spec', {
        method: 'POST',
        body,
        headers: { 'X-Founder-Signature': sig },
      });
      setResult({ run_id: data.run_id });
      setName('');
      setTargetMarket('');
      setTargetClient('');
      setRequirements('');
      setConstraints('');
      setBudgetUsd('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit spec.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/omega/commander" style={{ color: '#2563eb', fontSize: 14 }}>← Commander</Link>
      </div>
      <h1 style={{ fontSize: 24, marginBottom: 2 }}>New Founder Spec — Mode B</h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Command OMEGA to build a specific product. The run is founder-gated at every
        stage — nothing is built or spent until you approve each gate.
      </p>

      <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
        Product name *
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
        Target market *
        <input style={inputStyle} value={targetMarket} onChange={(e) => setTargetMarket(e.target.value)} />
      </label>
      <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
        Target client (optional)
        <input style={inputStyle} value={targetClient} onChange={(e) => setTargetClient(e.target.value)} />
      </label>
      <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
        Requirements (one per line)
        <textarea style={{ ...inputStyle, minHeight: 80 }} value={requirements} onChange={(e) => setRequirements(e.target.value)} />
      </label>
      <label style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
        Constraints (one per line)
        <textarea style={{ ...inputStyle, minHeight: 60 }} value={constraints} onChange={(e) => setConstraints(e.target.value)} />
      </label>
      <label style={{ display: 'block', marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
        Budget ceiling (USD)
        <input style={inputStyle} type="number" min="0" step="0.01" value={budgetUsd} onChange={(e) => setBudgetUsd(e.target.value)} />
      </label>

      <button
        onClick={submit}
        disabled={busy}
        style={{
          padding: '10px 20px',
          background: busy ? '#9ca3af' : '#2563eb',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 600,
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        {busy ? 'Submitting…' : 'Submit & start run'}
      </button>

      {error && <p style={{ color: '#dc2626', marginTop: 14 }}>{error}</p>}
      {result && (
        <div style={{ marginTop: 16, padding: 14, border: '1px solid #16a34a', borderRadius: 8, background: '#f0fdf4' }}>
          <div style={{ fontWeight: 600 }}>Run started.</div>
          <div style={{ fontFamily: 'monospace', fontSize: 13, margin: '4px 0' }}>{result.run_id}</div>
          <div style={{ fontSize: 13 }}>
            Parked at the Spec→Test gate. <Link to="/omega/pipeline">Open the pipeline monitor →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
